import React, {} from 'react'
import ImageSwiper from './ImageSwiper';
import css from './ListingCardBox.module.css';
import { useConfiguration } from '../../context/configurationContext';
import { displayPrice } from '../../util/configHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { formatMoney } from '../../util/currency';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { AvatarSmall, NamedLink, InlineTextButton } from '../../components';
import { createSlug, LISTING_PAGE_PARAM_TYPE_DRAFT } from '../../util/urlHelpers';
import { ensureListing } from '../../util/data';


function extractCityAndCountryFromAddress(address) {
  if (!address || typeof address !== 'string') return { city: '', country: '' };

  const streetIndicators = [
    'street', 'str.', 'str', 'road', 'rd.', 'rd', 'ave', 'avenue', 'av', 'blvd', 
    'boulevard', 'lane', 'ln', 'dr', 'drive', 'st', 'way', 'highway', 'hwy', 
    'plaza', 'square', 'place', 'crescent', 'circle', 'court', 'ct', 'terrace', 
    'ter', 'parkway', 'pkwy', 'trail', 'path', 'alley', 'row', 'close'
  ];

  const stateAbbreviations = [
    'al','ak','az','ar','ca','co','ct','de','fl','ga','hi','id','il','in','ia','ks','ky','la',
    'me','md','ma','mi','mn','ms','mo','mt','ne','nv','nh','nj','nm','ny','nc','nd','oh','ok',
    'or','pa','ri','sc','sd','tn','tx','ut','vt','va','wa','wv','wi','wy','dc'
  ];

  const parts = address.split(',')
    .map(p => p.trim())
    .filter(Boolean);

  if (!parts.length) return { city: '', country: '' };

  const filtered = parts.filter(part => {
    const lower = part.toLowerCase();
    const digits = part.replace(/[^\d]/g, '');
    const ratio = digits.length / part.length;

    if (ratio > 0.6 || /^\d{3,}$/.test(part)) return false;
    if (streetIndicators.some(ind => lower.includes(ind))) return false;
    if (part.length === 2 && stateAbbreviations.includes(lower)) return false;
    if (/^[a-z]\d[a-z]\s?\d[a-z]\d$/i.test(part)) return false; // Canadian postal
    if (/^[a-z]{1,2}\d{1,2}[a-z]?\s?\d[a-z]{2}$/i.test(part)) return false; // UK postal
    return true;
  });

  if (!filtered.length) return { city: '', country: '' };

  const country = cleanText(filtered[filtered.length - 1]);

  let city = '';
  for (let i = filtered.length - 2; i >= 0; i--) {
    const token = filtered[i].trim();

    if (/^[A-Z]{2}$/i.test(token) && stateAbbreviations.includes(token.toLowerCase())) continue;
    if (/^[A-Z]{2}\s+\S+$/i.test(token) && /\d/.test(token)) continue;

    const digits = token.replace(/[^\d]/g, '');
    if (digits.length / token.length > 0.5) continue;

    city = token;
    break;
  }

  if (!city) city = filtered[0];

  return {
    city: cleanText(city, { stripLeadingNumbers: true }),
    country
  };
}

function cleanText(str, opts = {}) {
  if (!str) return '';
  let s = String(str);
  if (opts.stripLeadingNumbers) s = s.replace(/^\d+\s*/, '');
  s = s.replace(/\s*\d+$/, '');
  s = s.trim();
  s = s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  return s;
}



const ListingCardBox = ({ listing, intl, reviews, toggleFavorites, profilePage }) => {
  const config = useConfiguration();
  const currentListing = ensureListing(listing);
  const fullAddress = currentListing?.attributes?.publicData?.location?.address || '';
  const { city, country } = extractCityAndCountryFromAddress(fullAddress);
  
  //console.log("Attributes", currentListing?.attributes?.publicData?.location?.address);

  const { attributes = {}, author, images = [] } = currentListing || {};
  const { title = '', price, publicData = {} } = attributes;
  const id = currentListing?.id?.uuid || '';
  const slug = createSlug(title);

 const listingReview = reviews?.filter(review => {
    const reviewListingId = review?.listing?.id?.uuid;
    return reviewListingId === currentListing?.id?.uuid;
  }) || [];

  const averageRating = listingReview.length > 0
    ? listingReview.reduce((sum, r) => {
        const rating = r?.attributes?.rating || 0;
        return sum + rating;
      }, 0) / listingReview.length
    : null;

  // Price formatter
  const priceData = (price, currency, intl) => {
    if (price && price.currency === currency) {
      const formattedPrice = formatMoney(intl, price);
      return { formattedPrice, priceTitle: formattedPrice };
    } else if (price) {
      return {
        formattedPrice: intl.formatMessage(
          { id: 'ListingCard.unsupportedPrice' },
          { currency: price.currency }
        ),
        priceTitle: intl.formatMessage(
          { id: 'ListingCard.unsupportedPriceTitle' },
          { currency: price.currency }
        ),
      };
    }
    return {};
  };

  // Renders price if allowed
  const PriceMaybe = ({ price, publicData, config, intl }) => {
    const { listingType } = publicData || {};
    const validListingTypes = config.listing.listingTypes;
    const foundConfig = validListingTypes.find(conf => conf.listingType === listingType);
    const showPrice = displayPrice(foundConfig);

    if (!showPrice || !price) return null;

    const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
    return (
      <div className={css.price}>
        <div className={css.priceValue} title={priceTitle}>
          {formattedPrice} / night
        </div>
      </div>
    );
  };

  return (
    <div className={css.cardBox} data="cardBox">
      <div className={css.cardImgBox} data="cardImgBox">
        {/*publicData?.guests_favorite &&
          <NamedLink
            name="ProfilePage"
            params={{ id: author?.id?.uuid }}
            className={css.chooseFvrt}
            title="chooseFvrt"
            // onClick={() => toggleFavorites(currentListing?.id)}
          >
            Guests Favorite
          </NamedLink>
        */}
        <div className={css.imgBox}>
          <ImageSwiper images={images} id={id} slug={slug} />
        </div>
      </div>
      <div className={css.cardDetails} data="cardDetails">
        <div className={css.titleRow} data="titleRow">
          <div className={css.title} data="title">{title}</div>
          {averageRating !== null && !isNaN(averageRating) ? (
            <div className={css.ratingText}>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
                <path d="M7.5 0L9.18386 5.18237H14.6329L10.2245 8.38525L11.9084 13.5676L7.5 10.3647L3.09161 13.5676L4.77547 8.38525L0.367076 5.18237H5.81614L7.5 0Z" fill="#8C8C8C"/>
              </svg>
              <span>{averageRating.toFixed(1)}</span>
            </div>
          ) : null}
        </div>
        <div className={css.userRow} data="userRow">
          <AvatarSmall user={author} className={css.userImage} />
          <NamedLink
            className={css.userProfileLink}
            name="ListingPage"
            params={{ id, slug }}
          >
            Hosted By - {author?.attributes?.profile?.displayName}
          </NamedLink>
        </div>

        <div className={css.cardPrice} data="cardPrice">
          <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
          {(city || country) && (
              <div className={css.cardAddress} data="cardAddress">
                {[...new Set([city, country].filter(Boolean).map(s => s.trim().toLowerCase()))]
                  .map(s => s.charAt(0).toUpperCase() + s.slice(1))
                  .join(', ')}
              </div>
          )}
        </div>
        <div className={css.cardFeatureRow} data="cardFeatureRow">
          <div className={css.featureBox}>Guests: <span className={css.num}>{publicData?.guest}</span></div>
          <div className={css.featureBox}>Rooms: <span className={css.num}>{publicData?.rooms}</span></div>
        </div>

        {!profilePage && 
          <NamedLink
            className={css.editListing}
            title="editListing"
            name="EditListingPage"
            params={{ id, slug, type: LISTING_PAGE_PARAM_TYPE_DRAFT, tab: 'details' }}
          >
              Edit Listing
          </NamedLink>
        }
      </div>
    </div>
  );
};

export default ListingCardBox;